/**
 * De dónde salen los colores y las letras de un tema.
 *
 * La regla que manda aquí: los valores se HORNEAN en `theme.roles`. Un tema no
 * guarda «el título usa el color de texto», guarda el color ya resuelto en el
 * rol del título. Por eso cambiar la paleta no repinta nada por sí solo: hay que
 * volver a pasar por `applyColorsToTheme`, y quien lo olvide verá que guardar el
 * color no cambia la página.
 *
 * Eso es exactamente lo que obligó a sacar este archivo del editor: el panel
 * necesita aplicar la misma derivación, y una segunda copia habría empezado a
 * divergir en el primer retoque.
 */
import type { PageTheme, ProjectTheme } from './types'
import { contrastRatio, distinctCardColor, distinctPrimaryColor, mostReadableOn } from './palettes'

export const COLOR_TOKENS: {
  id: string
  name: string
  /** Clave de catálogo. Ver `nameKey` en `GlobalColorComponent`. */
  nameKey?: string
  cssVar: string
  key: keyof PageTheme['colors']
}[] = [
  { id: 'primary', name: 'Primario', nameKey: 'panel.primary', cssVar: '--color-1', key: 'primary' },
  { id: 'secondary', name: 'Fondo alterno', nameKey: 'color.alternate', cssVar: '--color-2', key: 'backgroundSecondary' },
  { id: 'surface', name: 'Superficie', nameKey: 'panel.surface', cssVar: '--color-3', key: 'surface' },
  { id: 'neutralLight', name: 'Fondo', nameKey: 'panel.background', cssVar: '--color-4', key: 'background' },
  { id: 'neutralMid', name: 'Texto secundario', nameKey: 'color.mutedText', cssVar: '--color-5', key: 'muted' },
  { id: 'neutralDark', name: 'Texto', nameKey: 'el.texto', cssVar: '--color-6', key: 'text' },
  { id: 'accent1', name: 'Acento', nameKey: 'brand.accent', cssVar: '--color-7', key: 'accent' },
  { id: 'accent2', name: 'Tarjeta', nameKey: 'brand.card', cssVar: '--color-8', key: 'card' },
  { id: 'border', name: 'Bordes', nameKey: 'brand.borders', cssVar: '--color-9', key: 'border' },
]
export const COLOR_NAMES = COLOR_TOKENS.map((token) => token.name)
export const COLOR_KEYS = COLOR_TOKENS.map((token) => token.key)
export const COLORS = ['#FFFFFF', '#E8EDF3', '#C9D2DE', '#8B9BB1', '#3E4C63', '#1F2937', '#0A0F1A', '#E89B82']

function hexToRgb(hex: string): [number, number, number] | null {
  let h = hex.trim().replace('#', '')
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return null
  const n = parseInt(h, 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function mixHex(a: string, b: string, t: number): string {
  const ca = hexToRgb(a)
  const cb = hexToRgb(b)
  if (!ca || !cb) return a
  const ch = ca.map((v, i) => Math.max(0, Math.min(255, Math.round(v + (cb[i]! - v) * t))))
  return `#${ch.map((v) => v.toString(16).padStart(2, '0')).join('')}`
}

/**
 * The muted slot is the tertiary action colour: related to the palette's
 * primary and accent, but visibly different from both. It is intentionally
 * derived per palette so buttons do not fall back to the same generic grey.
 */
export function deriveMutedActionColor(primary: string, accent: string): string {
  return mixHex(primary, accent, 0.38)
}

/**
 * Thirty starting points for companies, not portfolios.
 *
 * Slots are fixed by `applyPresetToTheme`: [background, secondary, surface,
 * muted, ink, accent] — `ink` paints both body text and `primary`, so it is
 * always the darkest value, and the brand colour lives in `accent`.
 *
 * `ink` carries its family's hue rather than being black everywhere: it paints
 * `primary` too, so a neutral near-black in all thirty made every set of buttons
 * identical and the whole gallery read as one theme with a changing highlight.
 *
 * Three things shape the set, taken from 2026–27 trend reporting rather than
 * taste. Off-white over pure white (Pantone's Cloud Dancer year). Earth and
 * green over cool grey, following the wellness/natural turn. And deliberately
 * few generic corporate blues: when every SaaS, fintech and consultancy ships
 * the same indigo, it stops reading as a choice — the blues here are steel,
 * harbour and slate rather than the default #6366F1.
 */
const PRESET_DEFINITIONS = [
  // ── Neutros y monocromos ──────────────────────────────────────────────────
  { id: 'cloud-dancer', label: 'Cloud Dancer', f1: 'Inter', f2: 'Inter', pal: ['#FBFBF9', '#F2F2EE', '#E5E5DF', '#6C6C64', '#141413', '#3F5D57'], tags: ['moderno', 'sans-serif'] },
  { id: 'porcelain', label: 'Porcelana', f1: 'Plus Jakarta Sans', f2: 'Inter', pal: ['#FFFFFF', '#F6F7F8', '#E8EAED', '#6B7280', '#141A1E', '#2F6E5C'], tags: ['moderno', 'sans-serif'] },
  { id: 'graphite', label: 'Grafito', f1: 'Archivo', f2: 'Inter', pal: ['#FAFAFA', '#F0F0F0', '#E0E0E0', '#5F5F5F', '#141414', '#B0442F'], tags: ['moderno', 'sans-serif'] },
  { id: 'greige', label: 'Greige', f1: 'Manrope', f2: 'Inter', pal: ['#FAF9F6', '#F1EFE9', '#E3DFD5', '#6F6A5F', '#2A2419', '#9A7A46'], tags: ['moderno', 'calido', 'sans-serif'] },
  { id: 'ink', label: 'Tinta', f1: 'Figtree', f2: 'Inter', pal: ['#FCFCFC', '#F2F2F2', '#E2E2E2', '#5C5C5C', '#0A0A0A', '#D14026'], tags: ['moderno', 'sans-serif'] },

  // ── Verdes: bienestar, sostenibilidad, salud ──────────────────────────────
  { id: 'sage', label: 'Salvia', f1: 'Figtree', f2: 'Inter', pal: ['#FAFBF8', '#F0F3EC', '#E1E7DA', '#697065', '#1E2A1C', '#6E8A63'], tags: ['moderno', 'sans-serif'] },
  { id: 'olive', label: 'Olivo', f1: 'Karla', f2: 'Inter', pal: ['#FBFAF5', '#F3F1E6', '#E6E3D0', '#6E6B57', '#2A2A14', '#8A9142'], tags: ['calido', 'sans-serif'] },
  { id: 'moss', label: 'Musgo', f1: 'Urbanist', f2: 'Inter', pal: ['#F9FBF9', '#EEF3EE', '#DDE6DD', '#5F6B5E', '#14211A', '#3F7A4F'], tags: ['moderno', 'sans-serif'] },
  { id: 'pine', label: 'Pino', f1: 'Lexend', f2: 'Inter', pal: ['#F7FAF9', '#ECF2F0', '#D9E5E1', '#5C6B66', '#0E2620', '#16856B'], tags: ['moderno', 'sans-serif'] },
  { id: 'teal', label: 'Teal', f1: 'Space Grotesk', f2: 'Inter', pal: ['#F7FAFA', '#EBF2F2', '#D8E6E6', '#5B6A6A', '#0C2626', '#0E8F8F'], tags: ['moderno', 'sans-serif'] },
  { id: 'marine', label: 'Marino', f1: 'Sora', f2: 'Inter', pal: ['#F7F9FA', '#EAF0F2', '#D6E2E6', '#586669', '#0B2028', '#157F91'], tags: ['moderno', 'sans-serif'] },

  // ── Tierra: arcilla, cacao, arena ─────────────────────────────────────────
  { id: 'clay', label: 'Arcilla', f1: 'Manrope', f2: 'Inter', pal: ['#FCFAF8', '#F5EFEA', '#E9DED5', '#736760', '#2A1A12', '#B4633F'], tags: ['calido', 'sans-serif'] },
  { id: 'terracotta', label: 'Terracota', f1: 'Outfit', f2: 'Inter', pal: ['#FDFAF7', '#F7EEE7', '#EDDCD0', '#77675C', '#2E170F', '#C85A2B'], tags: ['calido', 'sans-serif'] },
  { id: 'cocoa', label: 'Cacao', f1: 'Epilogue', f2: 'Inter', pal: ['#FBF9F7', '#F2EDE8', '#E3DAD2', '#6E635B', '#241A14', '#8A5C3F'], tags: ['calido', 'sans-serif'] },
  { id: 'sand', label: 'Arena', f1: 'Public Sans', f2: 'Inter', pal: ['#FDFCF8', '#F6F2E8', '#EAE3D2', '#6F6858', '#2A2418', '#B08A4F'], tags: ['calido', 'sans-serif'] },
  { id: 'copper', label: 'Cobre', f1: 'Archivo', f2: 'Inter', pal: ['#FCFAF9', '#F4EEEB', '#E7DBD5', '#6F625C', '#2A170F', '#B9663A'], tags: ['calido', 'sans-serif'] },

  // ── Azules con carácter, no el azul por defecto ───────────────────────────
  { id: 'steel', label: 'Acero', f1: 'Inter', f2: 'Inter', pal: ['#F9FAFB', '#EEF1F4', '#DDE3E9', '#616C78', '#16202B', '#4A6E96'], tags: ['moderno', 'sans-serif'] },
  { id: 'harbor', label: 'Puerto', f1: 'IBM Plex Sans', f2: 'Inter', pal: ['#F8FAFB', '#EBF0F4', '#D8E1E8', '#5C6873', '#0D1F2D', '#2B6591'], tags: ['moderno', 'sans-serif'] },
  { id: 'denim', label: 'Denim', f1: 'Barlow', f2: 'Inter', pal: ['#F9FAFC', '#EFF2F6', '#DFE5EC', '#636D7A', '#141C26', '#5578A0'], tags: ['moderno', 'sans-serif'] },
  { id: 'cobalt', label: 'Cobalto', f1: 'Plus Jakarta Sans', f2: 'Inter', pal: ['#F8FAFC', '#EDF1F7', '#DCE4F0', '#5F6879', '#101C33', '#2456C4'], tags: ['moderno', 'sans-serif'] },

  // ── Gravedad: legal, seguros, finanzas ────────────────────────────────────
  { id: 'oxblood', label: 'Oxblood', f1: 'Libre Baskerville', f2: 'Public Sans', pal: ['#FCFAFA', '#F4EEEE', '#E7DADA', '#6E5F5F', '#241214', '#8E3038'], tags: ['elegante', 'serif'] },
  { id: 'burgundy', label: 'Burdeos', f1: 'Source Serif 4', f2: 'Inter', pal: ['#FBF9F9', '#F3ECED', '#E5D8DA', '#6B5C5E', '#22101A', '#8A2A44'], tags: ['elegante', 'serif'] },
  { id: 'aubergine', label: 'Berenjena', f1: 'Fraunces', f2: 'Karla', pal: ['#FBFAFB', '#F2EFF2', '#E3DCE4', '#665F68', '#1C1220', '#6A4468'], tags: ['elegante', 'serif'] },
  { id: 'bronze', label: 'Bronce', f1: 'EB Garamond', f2: 'Karla', pal: ['#FDFBF6', '#F5F0E4', '#E9E0C9', '#6F6753', '#26200F', '#9A7838'], tags: ['elegante', 'calido', 'serif'] },

  // ── Editorial y consultoría ───────────────────────────────────────────────
  { id: 'consulting', label: 'Consultoría', f1: 'IBM Plex Serif', f2: 'IBM Plex Sans', pal: ['#FAFAF9', '#F1F1EF', '#E2E2DE', '#67675F', '#141A1E', '#35586B'], tags: ['elegante', 'serif'] },
  { id: 'journal-pro', label: 'Editorial', f1: 'Spectral', f2: 'Inter', pal: ['#FCFBF9', '#F3F0EB', '#E4DFD6', '#6B655C', '#1A1714', '#40667C'], tags: ['elegante', 'serif'] },
  { id: 'institute', label: 'Instituto', f1: 'Domine', f2: 'Public Sans', pal: ['#FBFAF8', '#F1EFEA', '#E1DDD4', '#6A655C', '#16180F', '#4C6B49'], tags: ['elegante', 'serif'] },

  // ── Oscuros: OLED, producto, datos ────────────────────────────────────────
  { id: 'midnight', label: 'Medianoche', f1: 'Inter', f2: 'Inter', pal: ['#0B0C0E', '#141619', '#1E2126', '#8B939E', '#F5F6F7', '#4FD1C5'], tags: ['nocturno', 'sans-serif'] },
  { id: 'obsidian', label: 'Obsidiana', f1: 'Space Grotesk', f2: 'Inter', pal: ['#0A0A0A', '#151515', '#1F1F1F', '#8C8C8C', '#F2F2F2', '#C6A15B'], tags: ['nocturno', 'sans-serif'] },
  { id: 'forest-dark', label: 'Bosque', f1: 'Manrope', f2: 'Inter', pal: ['#0B100E', '#141A17', '#1D2621', '#8A968F', '#F1F4F2', '#5FA37E'], tags: ['nocturno', 'sans-serif'] },
]

export const PRESETS = PRESET_DEFINITIONS.map((preset) => ({
  ...preset,
  pal: preset.pal.map((color, index) => (
    index === 3 ? deriveMutedActionColor(preset.pal[4]!, preset.pal[5]!) : color
  )),
}))

/**
 * Las paletas. El `name` es un IDENTIFICADOR, no una etiqueta.
 *
 * ── Por qué no se enseña ────────────────────────────────────────────────────
 * Eran descripciones en español —«Tierra Cálida», «Latón y Obsidiana»— y en un
 * editor en inglés se leían como un descuido. Traducir sesenta y cinco no
 * arregla el fondo: el nombre de una paleta no dice nada que los colores no
 * digan ya, y quien elige una la elige mirándola. Así que se dejaron de
 * enseñar en los tres sitios que lo hacían.
 *
 * Se conservan porque hacen de identificador dentro de la sesión: el cajón de
 * paletas aplica por nombre y la pantalla de crear proyecto guarda cuál está
 * elegida. No viajan al servidor —allí van los colores— así que cambiar uno no
 * rompe nada guardado, pero sí rompería la selección de quien lo tenga abierto.
 *
 * Si algún día vuelven a la pantalla, hay que convertirlos a claves de catálogo
 * primero: en cuanto se pintan, dejan de ser un id y pasan a ser texto.
 */
const PALETTE_DEFINITIONS: { name: string; colors: string[]; tags: string[] }[] = [
  { name: 'Tech Indigo (SaaS)', colors: ['#FFFFFF', '#F4F4F5', '#E4E4E7', '#D4D4D8', '#52525B', '#6366F1', '#09090B', '#10B981'], tags: ['moderno', 'saas'] },
  { name: 'Tierra Cálida (Ed.)', colors: ['#FDFBF7', '#F5EFEB', '#E8DEC9', '#D2C4AF', '#736B5E', '#A75D3D', '#251E18', '#5E7A68'], tags: ['elegante', 'calido'] },
  { name: 'Latón y Obsidiana', colors: ['#FAF9F5', '#F0EEE4', '#E5E0CF', '#C8B195', '#6B6658', '#9D8252', '#1C1A17', '#4A5D4E'], tags: ['elegante', 'lujo'] },

  // ── 50 paletas añadidas para 2026–27 ──────────────────────────────────────
  // Ranuras: [fondo, fondo2, superficie, borde, tenue, PRIMARIO, texto, acento].
  // A diferencia de las combinaciones, aquí `primario` y `texto` son ranuras
  // distintas — por eso estas pueden llevar un primario saturado sin tocar la
  // legibilidad del cuerpo, que es de donde sale la variedad real.

  // Azules
  { name: 'Azul Prusia', colors: ['#F7F9FB', '#EDF2F7', '#DDE7F0', '#C7D6E4', '#5A6A7A', '#1B3A5C', '#0D1620', '#C98A3E'], tags: ['moderno', 'elegante'] },
  { name: 'Índigo Profundo', colors: ['#F8F8FC', '#EFEFF9', '#E0E0F3', '#CBCBE8', '#5E5E78', '#3730A3', '#12121F', '#F59E0B'], tags: ['moderno', 'saas'] },
  { name: 'Azul Hielo', colors: ['#FAFDFF', '#EFF7FC', '#DDEEF7', '#C4E0EE', '#566873', '#0E7490', '#0B1A20', '#F97316'], tags: ['moderno', 'minimo'] },
  { name: 'Cobalto Eléctrico', colors: ['#F9FAFE', '#EFF3FD', '#DEE7FA', '#C6D5F4', '#5A6478', '#1D4ED8', '#0C1220', '#22C55E'], tags: ['moderno', 'tech'] },
  { name: 'Azul Petróleo', colors: ['#F7FAFA', '#EBF2F2', '#D9E7E7', '#BFD5D5', '#54666A', '#155E63', '#0A1719', '#E4A853'], tags: ['moderno', 'elegante'] },
  { name: 'Noche Azul', colors: ['#0A1018', '#111B26', '#1A2836', '#243747', '#8DA1B4', '#4A9EDA', '#F0F5FA', '#F0B429'], tags: ['nocturno', 'tech'] },

  // Turquesas y cianes
  { name: 'Turquesa Nórdico', colors: ['#F6FBFB', '#E9F5F5', '#D5EBEA', '#B6DCDA', '#4F6866', '#0D7C7C', '#0A1A1A', '#E07A5F'], tags: ['moderno', 'salud'] },
  { name: 'Verdemar', colors: ['#F6FBF9', '#E8F5F0', '#D2EBE2', '#B0DBCC', '#4E6B62', '#0F766E', '#0A1815', '#D97706'], tags: ['moderno', 'organico'] },
  { name: 'Cian Clínico', colors: ['#FAFDFE', '#EEF8FB', '#DAEFF6', '#BEE2EE', '#526670', '#0891B2', '#0A1519', '#16A34A'], tags: ['moderno', 'salud'] },
  { name: 'Teal Profundo', colors: ['#F5FAFA', '#E7F2F2', '#CFE6E6', '#AFD4D4', '#4A6464', '#134E4A', '#081514', '#F4A261'], tags: ['elegante', 'moderno'] },
  { name: 'Aguamarina Suave', colors: ['#F8FCFC', '#EDF7F7', '#DBEFEF', '#C0E3E3', '#556B6B', '#2A9D8F', '#0D1A19', '#264653'], tags: ['moderno', 'salud'] },

  // Verdes
  { name: 'Verde Bosque', colors: ['#F7FAF7', '#EDF4ED', '#DAE8DA', '#BDD5BD', '#516551', '#166534', '#0A1610', '#CA8A04'], tags: ['moderno', 'organico'] },
  { name: 'Verde Menta', colors: ['#F8FDFA', '#EDF9F2', '#D9F2E4', '#B9E6CD', '#516B5D', '#059669', '#0B1A13', '#7C3AED'], tags: ['moderno', 'salud'] },
  { name: 'Oliva Militar', colors: ['#FAFAF5', '#F2F2E7', '#E4E4CF', '#CFCFAF', '#63634B', '#4D5320', '#171808', '#B45309'], tags: ['calido', 'tierra'] },
  { name: 'Esmeralda', colors: ['#F6FBF8', '#E9F6EF', '#D2EEDF', '#ADDFC5', '#4C665A', '#047857', '#08170F', '#F59E0B'], tags: ['elegante', 'moderno'] },
  { name: 'Salvia Suave', colors: ['#FAFBF9', '#F1F4EE', '#E2E9DD', '#CBD8C4', '#61705C', '#5F7A55', '#171C14', '#A8763E'], tags: ['calido', 'organico'] },
  { name: 'Lima Corporativa', colors: ['#FBFCF6', '#F4F7E9', '#E9F0D2', '#D6E3AF', '#646B4E', '#4D7C0F', '#151A0A', '#0369A1'], tags: ['moderno', 'vibrante'] },

  // Ámbar y mostaza
  { name: 'Ámbar Cálido', colors: ['#FEFBF5', '#FCF4E5', '#F7E8C9', '#EDD49C', '#6E6350', '#B45309', '#1C1710', '#0F766E'], tags: ['calido', 'moderno'] },
  { name: 'Mostaza Suave', colors: ['#FDFCF4', '#F8F5E3', '#F0EBC8', '#E0D69B', '#69654C', '#A16207', '#1A180C', '#3F6212'], tags: ['calido', 'retro'] },
  { name: 'Dorado Arena', colors: ['#FDFBF6', '#F7F1E4', '#EDE2C9', '#DCCCA3', '#6B6350', '#8A6D2F', '#191710', '#3B5D6B'], tags: ['elegante', 'lujo'] },
  { name: 'Ocre', colors: ['#FCFAF6', '#F5EFE4', '#EAE0CB', '#D8C7A5', '#6B6250', '#9A6B24', '#1A160E', '#2F5D50'], tags: ['calido', 'tierra'] },

  // Naranjas y corales
  { name: 'Naranja Quemado', colors: ['#FDFAF7', '#F9EEE6', '#F1DBCB', '#E3BFA4', '#6E5F54', '#C2410C', '#1C130E', '#0E7490'], tags: ['calido', 'vibrante'] },
  { name: 'Coral Sobrio', colors: ['#FEFAF9', '#FBEFEC', '#F5DCD6', '#EBC0B6', '#71605C', '#D1543C', '#1E1412', '#2A6F6B'], tags: ['calido', 'boutique'] },
  { name: 'Cobre Vivo', colors: ['#FCFAF8', '#F6EFE9', '#EBDCD1', '#D9BFAC', '#6D6055', '#A85A2E', '#1A130E', '#37605C'], tags: ['calido', 'industrial'] },
  { name: 'Melocotón', colors: ['#FFFBF8', '#FDF1E9', '#F9E0D0', '#F0C4A9', '#736158', '#D57442', '#20150F', '#4A6FA5'], tags: ['calido', 'boutique'] },

  // Rojos
  { name: 'Rojo Ladrillo', colors: ['#FCF9F8', '#F6EDEB', '#EAD7D3', '#D8B5AF', '#6B5A57', '#9B3B2E', '#18100E', '#3D6B4A'], tags: ['calido', 'industrial'] },
  { name: 'Carmesí Sobrio', colors: ['#FDFAFA', '#F7EDEE', '#EDD6D8', '#DDB2B6', '#6C5A5C', '#B91C1C', '#180F10', '#1E5F74'], tags: ['moderno', 'vibrante'] },
  { name: 'Rojo Vino', colors: ['#FBF8F8', '#F3EAEB', '#E5D1D4', '#CFAAAF', '#665356', '#7F1D3A', '#160C10', '#B08A4F'], tags: ['elegante', 'lujo'] },
  { name: 'Granate Clásico', colors: ['#FCFAFA', '#F4EEEE', '#E7D9D9', '#D2B7B7', '#675757', '#6B2434', '#140D0F', '#3F5D57'], tags: ['elegante'] },

  // Rosas y magentas
  { name: 'Rosa Polvo', colors: ['#FEFBFC', '#FAEFF3', '#F2DBE3', '#E5BCCB', '#6E5C63', '#A34766', '#1B1114', '#3F6B6B'], tags: ['elegante', 'boutique'] },
  { name: 'Magenta Sobrio', colors: ['#FCFAFC', '#F5EEF5', '#E9DAEA', '#D5B9D7', '#655766', '#9D2B78', '#160E14', '#0E7490'], tags: ['moderno', 'vibrante'] },
  { name: 'Frambuesa', colors: ['#FDFAFB', '#F8EDF1', '#EFD6DF', '#DFB2C2', '#6B575E', '#B02A5B', '#190E13', '#2F6E5C'], tags: ['moderno', 'boutique'] },

  // Violetas
  { name: 'Violeta Profundo', colors: ['#FAFAFD', '#F1EFFA', '#E2DEF4', '#C9C2EA', '#5E5A74', '#5B21B6', '#110E1C', '#F59E0B'], tags: ['moderno', 'tech'] },
  { name: 'Lavanda Gris', colors: ['#FBFBFD', '#F3F3F8', '#E5E5EF', '#CFCFE0', '#605F71', '#6D6A9B', '#141320', '#B4794F'], tags: ['minimo', 'elegante'] },
  { name: 'Ciruela', colors: ['#FBFAFC', '#F2EEF4', '#E3D9E8', '#CBBAD4', '#635A69', '#6B2D5C', '#150E16', '#C08A3E'], tags: ['elegante', 'lujo'] },
  { name: 'Índigo Violeta', colors: ['#F9F9FD', '#EFEEFA', '#DFDCF5', '#C5C0EC', '#5C5A78', '#4C1D95', '#0F0D1C', '#14B8A6'], tags: ['moderno', 'saas'] },

  // Marrones y tierra
  { name: 'Café Tostado', colors: ['#FBF9F7', '#F2EDE8', '#E3D8CF', '#CEBBAB', '#665C53', '#5B3A24', '#150F0B', '#7A9163'], tags: ['calido', 'tierra'] },
  { name: 'Nuez', colors: ['#FCFAF8', '#F4EFE9', '#E7DCD0', '#D3C1AE', '#6A6055', '#7A5230', '#18120C', '#3E6B62'], tags: ['calido', 'tierra'] },
  { name: 'Caramelo', colors: ['#FDFBF7', '#F8F1E7', '#EFE0CB', '#DFC7A4', '#6E6352', '#9C6B33', '#1A150E', '#2F5D6B'], tags: ['calido', 'organico'] },
  { name: 'Arcilla Oscura', colors: ['#FAF8F6', '#F0EBE6', '#DFD5CC', '#C6B6A9', '#645A52', '#6B4232', '#16100C', '#4F6B52'], tags: ['calido', 'tierra'] },

  // Neutros
  { name: 'Gris Cemento', colors: ['#FAFAFA', '#F0F1F1', '#E1E3E3', '#C9CCCC', '#5C6161', '#3F4645', '#101212', '#B0442F'], tags: ['minimo', 'industrial'] },
  { name: 'Blanco Hueso', colors: ['#FDFCF9', '#F5F3EC', '#E9E5DA', '#D6D0C0', '#67635A', '#2E2C26', '#131210', '#8A7A46'], tags: ['minimo', 'calido'] },
  { name: 'Piedra', colors: ['#FAFAF9', '#F2F1EF', '#E4E2DE', '#CDCAC4', '#605E58', '#44413B', '#131211', '#5F7A55'], tags: ['minimo', 'organico'] },
  { name: 'Niebla', colors: ['#FBFCFC', '#F2F5F5', '#E4E9E9', '#CDD5D5', '#5D6666', '#37474F', '#101414', '#26A69A'], tags: ['minimo', 'moderno'] },
  { name: 'Negro Suave', colors: ['#FCFCFC', '#F3F3F3', '#E5E5E5', '#CFCFCF', '#5E5E5E', '#171717', '#0A0A0A', '#D14026'], tags: ['minimo', 'agencia'] },

  // Oscuros
  { name: 'Carbón Ámbar', colors: ['#0C0B0A', '#151311', '#201D19', '#2E2925', '#A09789', '#E0A458', '#F7F4EF', '#7A9163'], tags: ['nocturno', 'calido'] },
  { name: 'Noche Esmeralda', colors: ['#08110D', '#0F1B15', '#16261E', '#20342A', '#8FA398', '#34D399', '#F0F5F2', '#FBBF24'], tags: ['nocturno', 'organico'] },
  { name: 'Noche Violeta', colors: ['#0B0A11', '#13111C', '#1C1928', '#282338', '#9791AD', '#A78BFA', '#F4F2FA', '#22D3EE'], tags: ['nocturno', 'tech'] },
  { name: 'Grafito Cian', colors: ['#0A0C0D', '#121618', '#1B2124', '#262E32', '#93A1A6', '#22D3EE', '#F2F6F7', '#F472B6'], tags: ['nocturno', 'cyberpunk'] },
  { name: 'Tinta Marina', colors: ['#080F16', '#101A24', '#182632', '#223343', '#8DA0B0', '#60A5FA', '#EFF4F9', '#F59E0B'], tags: ['nocturno', 'saas'] },
  // ── Producto / SaaS contemporáneo ─────────────────────────────────────────
  // Inspiradas en cómo pintan hoy los productos que la gente reconoce. Los
  // nombres son descriptivos a propósito: evocan el estilo sin dar a entender
  // que la marca respalda la plantilla.

  { name: 'Arcilla Cálida', colors: ['#FAF9F5', '#F2EFE8', '#E8E3D8', '#D8D0C0', '#6B665C', '#C2603F', '#191917', '#3F6B5C'], tags: ['calido', 'moderno', 'saas'] },
  { name: 'Neutro Producto', colors: ['#FFFFFF', '#F5F5F7', '#EBEBED', '#D8D8DC', '#6E6E73', '#0071E3', '#1D1D1F', '#34C759'], tags: ['minimo', 'moderno', 'saas'] },
  { name: 'Verde Trading', colors: ['#FFFFFF', '#F6F8F6', '#EBF1EC', '#D6E4D8', '#5C6B5E', '#00843D', '#101410', '#1A1A1A'], tags: ['moderno', 'saas'] },
  { name: 'Verde Comercio', colors: ['#F7FAF8', '#EDF5F0', '#DDEBE3', '#C4DBCF', '#556B60', '#00694F', '#0E1512', '#5C6AC4'], tags: ['moderno', 'saas'] },
  { name: 'Verde Marketplace', colors: ['#F9FBF6', '#F1F6EA', '#E3EDD6', '#CCDEB6', '#5F6B4F', '#3F7A2E', '#131A0F', '#0F766E'], tags: ['moderno', 'saas'] },
  { name: 'Índigo Producto', colors: ['#FCFCFD', '#F4F4F8', '#E8E8F1', '#D5D5E6', '#5E5E70', '#4F46E5', '#111119', '#14B8A6'], tags: ['moderno', 'saas'] },
  { name: 'Violeta Pago', colors: ['#FBFBFF', '#F3F3FC', '#E7E7F8', '#D3D3F0', '#5C5C74', '#4F46C8', '#0F0F1A', '#00A870'], tags: ['moderno', 'saas'] },
  { name: 'Mono Deploy', colors: ['#FFFFFF', '#FAFAFA', '#F0F0F0', '#DEDEDE', '#5A5A5A', '#000000', '#0A0A0A', '#0070F3'], tags: ['minimo', 'moderno', 'saas'] },

  // Lima/volt: en claro solo funciona como acento. El primario va oscuro para
  // que los botones se lean, y el contraste automático pone texto oscuro encima
  // del lima — pintarlo de primario daría 1.5:1 y sería ilegible en blanco.
  { name: 'Volt Claro', colors: ['#FBFCF7', '#F4F7E8', '#EAEFD3', '#D9E2AF', '#63684F', '#1F2415', '#0E1109', '#C8E85A'], tags: ['moderno', 'vibrante', 'saas'] },
  { name: 'Volt Oliva', colors: ['#FAFBF5', '#F2F5E6', '#E6ECCF', '#D2DCA8', '#63684D', '#4F6B12', '#141709', '#D7FF64'], tags: ['moderno', 'vibrante'] },
  { name: 'Volt Nocturno', colors: ['#0C0E08', '#141810', '#1D2317', '#2A3221', '#9AA48C', '#CFF551', '#F4F7EC', '#7DD3A0'], tags: ['nocturno', 'vibrante', 'saas'] },
  { name: 'Grafito Producto', colors: ['#FCFCFC', '#F4F4F5', '#E9E9EB', '#D7D7DA', '#5B5B60', '#18181B', '#0B0B0D', '#F97316'], tags: ['minimo', 'moderno', 'saas'] },
  // Suaves: pastel sobre crema (ver las mismas en `palettes.ts`). Ranuras como
  // arriba: [fondo, fondo2, superficie, borde, tenue, PRIMARIO, texto, acento].
  { name: 'Cuarzo Rosa', colors: ['#FFFAF9', '#FBEFEE', '#F5E1E0', '#EED8D6', '#7A6164', '#B8607A', '#3A2528', '#D9A3A8'], tags: ['suave'] },
  { name: 'Palo de Rosa', colors: ['#FDF8F6', '#F8ECE8', '#F1DDD6', '#EAD5CD', '#7A6660', '#A8656F', '#3B2A26', '#C9928A'], tags: ['suave', 'calido'] },
  { name: 'Peonía y Arena', colors: ['#FCF9F4', '#F6EEE4', '#EEDFD0', '#E6D7C6', '#76695C', '#C0727F', '#33281F', '#B08A64'], tags: ['suave', 'calido'] },
  { name: 'Rosa Viejo', colors: ['#FBF7F7', '#F3E9EA', '#E8D6D8', '#E2CFD2', '#6E5A5F', '#8E4A5C', '#2A1A1E', '#C48A97'], tags: ['suave', 'elegante'] },
  { name: 'Lavanda', colors: ['#FAF9FD', '#F1EEF9', '#E5E0F3', '#DEDAEE', '#635E78', '#7A68B8', '#25213A', '#B7A6E0'], tags: ['suave'] },
  { name: 'Lila Bruma', colors: ['#FCFAFC', '#F5EEF5', '#EADDEA', '#E3D6E3', '#6F5F70', '#94608F', '#2E2230', '#C9A2C4'], tags: ['suave', 'elegante'] },
  { name: 'Durazno', colors: ['#FFF9F5', '#FCEEE4', '#F7DECC', '#F0D6C4', '#7C6556', '#C8673F', '#3A2418', '#F2B391'], tags: ['suave', 'calido'] },
  { name: 'Coral Suave', colors: ['#FFF8F6', '#FDEDE8', '#F9DBD2', '#F2D3C9', '#7E625B', '#D9614A', '#3B1F19', '#F5A58F'], tags: ['suave', 'calido'] },
  { name: 'Mantequilla', colors: ['#FFFDF6', '#FBF6E2', '#F5ECC8', '#EDE3BF', '#716A4E', '#9C7A16', '#2E2814', '#E8C766'], tags: ['suave', 'calido'] },
  { name: 'Salvia', colors: ['#F8FAF6', '#EEF3EA', '#E0E9DA', '#D9E3D2', '#5F6B5D', '#5E7A5A', '#1F2A1E', '#A9BFA0'], tags: ['suave'] },
  { name: 'Menta', colors: ['#F6FBF9', '#E9F5F0', '#D6ECE3', '#D0E6DC', '#56695F', '#2F8A6B', '#14281F', '#8FD3B6'], tags: ['suave'] },
  { name: 'Vidrio de Mar', colors: ['#F6FBFA', '#E8F4F2', '#D3EAE6', '#CDE4E0', '#56696A', '#3F8D86', '#12282A', '#A6D8D0'], tags: ['suave'] },
  { name: 'Azul Empolvado', colors: ['#F8FBFD', '#ECF3F9', '#DCE9F3', '#D4E3EF', '#5A6875', '#4F7FA6', '#16222E', '#9EC3E0'], tags: ['suave'] },
  { name: 'Noche Rosa', colors: ['#151012', '#1E171A', '#291F23', '#3A2C31', '#A8979C', '#E8A0B4', '#F6ECEE', '#E8A0B4'], tags: ['nocturno', 'suave'] },
]

export const PALETTES = PALETTE_DEFINITIONS.map((palette) => {
  const colors = palette.colors.map((color, index) => (
    index === 4 ? deriveMutedActionColor(palette.colors[5]!, palette.colors[7]!) : color
  ))
  colors[5] = distinctPrimaryColor(colors[5]!, colors[7]!)
  return { ...palette, colors }
})

export const DRAWER_OPTIONS = PALETTES.map((p) => p.name)

export function themeColorsToArray(theme: PageTheme): string[] {
  return COLOR_KEYS.map((key) => theme.colors[key])
}

const BORDER_INDEX = COLOR_KEYS.indexOf('border')

/**
 * A subtle border that lifts cards off the background: the surface nudged toward the
 * muted tone, plus a faint tint of the primary so it has a bit of the theme's character
 * without being loud. Derived per palette so every combination gets a fitting border.
 */
export function deriveBorderColor(colors: PageTheme['colors']): string {
  return mixHex(mixHex(colors.surface, colors.muted, 0.22), colors.primary, 0.1)
}

/** El botón pinta su relleno con el color de marca —el primario—. */
function fillsWithButton(styles: { background?: string; backgroundColor?: string } | undefined) {
  const fill = styles?.backgroundColor ?? styles?.background ?? ''
  return fill.includes('var(--wb-primary)')
}

/* Lo que de un botón lleva el color de la marca. */
const BUTTON_COLOR_KEYS = ['background', 'backgroundColor', 'color', 'borderColor', 'borderTop', 'borderRight', 'borderBottom', 'borderLeft', 'outline', 'boxShadow'] as const

/**
 * Los botones, al color de marca: el primario.
 *
 * Los de fábrica se pintaban con `--wb-muted`, que los bloques usan también
 * como gris de texto secundario —la fecha, la etiqueta, el párrafo apagado—,
 * así que el color del botón y el del texto secundario eran uno solo y no se
 * podía elegir uno sin arrastrar el otro. Y un botón que alguien había pasado
 * al acento se quedaba en el acento aunque la paleta trajera su botón.
 *
 * Al aplicar colores, lo que en un botón apuntaba a `muted` o al acento pasa a
 * apuntar al primario, sin tocar la forma: un contorno sigue siendo contorno,
 * y lo que va en la tinta —el borde de un botón de línea— sigue en la tinta.
 */
function retargetButton<T extends Record<string, unknown>>(styles: T | undefined, kind: 'primary' | 'secondary' = 'primary'): T {
  const next = { ...(styles ?? {}) } as Record<string, unknown>
  for (const key of BUTTON_COLOR_KEYS) {
    const value = next[key]
    if (typeof value === 'string') next[key] = value.replace(/var\(--wb-(muted|accent)\)/g, 'var(--wb-primary)')
  }
  return withoutLiteralButtonColors(next, kind) as T
}

/** Un color escrito a mano: un hex o un rgb(), no una variable ni «transparent». */
const isLiteralColor = (value: unknown): value is string =>
  typeof value === 'string' && /^\s*(#[0-9a-f]{3,8}|rgba?\(|hsla?\()/i.test(value)

/**
 * Los colores escritos a mano de un botón de fábrica, a variables del tema.
 *
 * Los temas de antes guardaban el botón con su azul literal —`#2563EB`—, y
 * `retargetButton` sólo movía variables: al elegir otro estilo global el tema
 * cambiaba de color y el botón seguía azul, para siempre. Aplicar colores es
 * repintar el sitio, así que lo literal del botón pasa al token: el relleno y
 * el borde del principal a la marca; el secundario a su contorno de siempre
 * (fondo transparente, letra y borde en la marca). Las variables que ya había
 * no se tocan, y la forma tampoco.
 */
function withoutLiteralButtonColors(styles: Record<string, unknown>, kind: 'primary' | 'secondary') {
  const next = { ...styles }
  if (kind === 'primary') {
    for (const key of ['background', 'backgroundColor', 'borderColor'] as const) {
      if (isLiteralColor(next[key])) next[key] = 'var(--wb-primary)'
    }
  } else {
    for (const key of ['background', 'backgroundColor'] as const) {
      if (isLiteralColor(next[key])) next[key] = 'transparent'
    }
    for (const key of ['color', 'borderColor'] as const) {
      if (isLiteralColor(next[key])) next[key] = 'var(--wb-primary)'
    }
  }
  /* El resplandor del azul viejo —`rgba(37, 99, 235, …)`— no sigue a nada. */
  if (typeof next.boxShadow === 'string' && /rgba?\(\s*37\s*,\s*99\s*,\s*235/.test(next.boxShadow)) next.boxShadow = 'none'
  return next
}

/**
 * La letra que mejor se lee sobre `surface`, como variable del tema cuando es
 * uno de sus colores —así sigue a la paleta si se retoca un color— o como
 * blanco o casi negro si ninguno llega.
 */
export function readableVar(surface: string, colors: PageTheme['colors']): string {
  /* En un botón de marca, blanco (o el fondo, si es claro) mientras llegue a
     3:1: ver `buttonWantsLightText`. Con la regla de abajo sola, un azul medio
     como #2485E7 se llevaba letra casi negra por medio punto de contraste. */
  if (contrastRatio(surface, '#FFFFFF') >= 3) {
    return contrastRatio(colors.background, '#FFFFFF') < 1.25 && contrastRatio(surface, colors.background) >= 3
      ? 'var(--wb-background)'
      : '#FFFFFF'
  }
  const candidates: [string, string][] = [
    ['var(--wb-background)', colors.background],
    ['var(--wb-text)', colors.text],
    ['#FFFFFF', '#FFFFFF'],
    ['#0B0B0B', '#0B0B0B'],
  ]
  const good = candidates.find(([, value]) => contrastRatio(surface, value) >= 4.5)
  if (good) return good[0]
  const best = mostReadableOn(surface, candidates.map(([, value]) => value))
  return candidates.find(([, value]) => value === best)![0]
}

/** La letra encima de un botón con relleno de marca; los de contorno no la cambian. */
function withButtonText<T extends { background?: string; backgroundColor?: string; color?: string }>(styles: T, color: string): T {
  return fillsWithButton(styles) ? { ...styles, color } : styles
}

export function applyColorsToTheme(theme: PageTheme, values: string[], presetId = 'custom'): PageTheme {
  const colors = COLOR_KEYS.reduce<PageTheme['colors']>((acc, key, index) => ({
    ...acc,
    [key]: values[index] ?? theme.colors[key],
  }), { ...theme.colors })

  /* Primario y acento son dos papeles, aunque nazcan del mismo tono. El acento
     conserva la elección y el botón recibe una variante un poco más clara. */
  colors.primary = distinctPrimaryColor(colors.primary, colors.accent)

  // When a preset/palette is applied (no explicit border value), derive a fitting border
  // from the new palette. When the border is edited directly, the value is passed through.
  if (values[BORDER_INDEX] === undefined) {
    colors.border = deriveBorderColor(colors)
  }

  /* Es una garantía del tema, no sólo de las paletas del selector: presets,
     colores elegidos a mano y flujos de creación pasan por aquí. De ese modo
     una tarjeta nunca desaparece contra el fondo aunque no tenga borde ni
     sombra. */
  colors.card = distinctCardColor({
    card: colors.card,
    background: colors.background,
    alternate: colors.backgroundSecondary,
    ink: colors.text,
  })

  /* El párrafo va en la tinta, tal cual. Era una mezcla con el fondo —un gris
     que no era ningún color de la paleta—: el panel decía «Text» y la página
     pintaba otra cosa. Quien lo quiera más suave elige otro color. */
  const paragraphColor = 'var(--wb-text)'
  const projectTheme = theme as Partial<ProjectTheme>
  /* La letra del botón no es un color de la paleta: la que mejor se lea sobre
     su relleno —el fondo, la tinta, blanco o negro—. Un botón amarillo lleva
     letra oscura; uno negro, clara. */
  const onButton = readableVar(colors.primary, colors)
  /* Enlaces en el acento sólo si se leen; si no —un amarillo sobre blanco—,
     en la tinta. */
  const linkColor = contrastRatio(colors.accent, colors.background) >= 3 ? colors.accent : colors.text
  return {
    ...theme,
    presetId,
    colors,
    roles: {
      ...theme.roles,
      section: { ...theme.roles.section, background: colors.background, color: colors.text },
      heading1: { ...theme.roles.heading1, color: colors.text },
      heading2: { ...theme.roles.heading2, color: colors.text },
      heading3: { ...theme.roles.heading3, color: colors.text },
      heading4: { ...theme.roles.heading4, color: colors.text },
      heading5: { ...theme.roles.heading5, color: colors.text },
      heading6: { ...theme.roles.heading6, color: colors.text },
      paragraph: { ...theme.roles.paragraph, color: paragraphColor },
      eyebrow: { ...theme.roles.eyebrow, color: 'var(--wb-text)', opacity: theme.roles.eyebrow.opacity ?? '0.6' },
      smallText: { ...theme.roles.smallText, color: 'var(--wb-text)', opacity: theme.roles.smallText.opacity ?? '0.6' },
      /* El relleno del botón no se toca: es `var(--wb-muted)` y sigue solo a la
         paleta. Reescribirlo deshacía la forma elegida en Buttons —un botón con
         contorno volvía a ser sólido al cambiar de colores—. Sólo su letra. */
      buttonPrimary: withButtonText(retargetButton(theme.roles.buttonPrimary), onButton),
      buttonSecondary: retargetButton(theme.roles.buttonSecondary, 'secondary'),
      card: { ...theme.roles.card, background: colors.card, color: colors.text, borderColor: colors.border },
      image: { ...theme.roles.image },
      /* Sobre su caja, que es el fondo alterno y no el de la página. */
      icon: { ...theme.roles.icon, color: contrastRatio(linkColor, colors.backgroundSecondary) >= 3 ? linkColor : colors.text, background: colors.backgroundSecondary },
      badge: { ...theme.roles.badge, color: colors.text, background: colors.surface, borderColor: colors.border },
      rating: { ...theme.roles.rating, color: colors.accent },
      avatarGroup: { ...theme.roles.avatarGroup },
      stat: { ...theme.roles.stat, background: colors.card, color: colors.text, borderColor: colors.border },
      link: { ...theme.roles.link, color: linkColor },
      /* La navegación sobre el fondo de la página, no sobre el acento: una
         barra de color de marca es de otra época. */
      navigation: { ...theme.roles.navigation, background: colors.background, color: colors.text, borderColor: colors.border },
      footer: { ...theme.roles.footer, background: colors.text, color: colors.background },
    },
    ...(Array.isArray(projectTheme.colorComponents)
      ? {
          colorComponents: projectTheme.colorComponents.map((component) => {
            const value = colors[component.id as keyof PageTheme['colors']]
            return value ? { ...component, value } : component
          }),
        }
      : {}),
    ...(Array.isArray(projectTheme.typographyComponents)
      ? {
          /* Los títulos también en sus estilos de texto, no sólo en el rol: el
             estilo guardado ganaba al rol, y un H1 se quedaba con el color de
             cuando se creó el proyecto aunque la paleta cambiara. */
          typographyComponents: projectTheme.typographyComponents.map((component) => (
            component.role === 'eyebrow' || component.role === 'smallText'
              ? { ...component, styles: { ...component.styles, color: 'var(--wb-text)', opacity: component.styles.opacity ?? '0.6' } }
              : component.role === 'paragraph'
              ? { ...component, styles: { ...component.styles, color: paragraphColor } }
              : component.role && /^heading[1-6]$/.test(component.role)
                ? { ...component, styles: { ...component.styles, color: 'var(--wb-text)' } }
                : component
          )),
        }
      : {}),
    ...(Array.isArray(projectTheme.buttonComponents)
      ? {
          buttonComponents: projectTheme.buttonComponents.map((component) => {
            if (component.id === 'button-primary') return { ...component, styles: withButtonText(retargetButton(component.styles), onButton) }
            if (component.id === 'button-secondary') return { ...component, styles: retargetButton(component.styles, 'secondary') }
            /* El enlace, como su rol: el acento si se lee, si no la tinta. */
            if (component.id === 'button-link' && isLiteralColor(component.styles?.color)) {
              return { ...component, styles: { ...component.styles, color: linkColor === colors.accent ? 'var(--wb-accent)' : 'var(--wb-text)' } }
            }
            return component
          }),
        }
      : {}),
  }
}

/** Los títulos grandes: los únicos que llevan la letra de títulos de una pareja de cursiva. */
const DISPLAY_ROLES = new Set(['heading1', 'heading2'])

/**
 * `displayOnly`: la letra de títulos va sólo en H1 y H2, a peso normal; lo
 * demás que la llevaba (H3-H6, antetítulo, insignia, cifra) toma la del cuerpo.
 * Es para las cursivas de caligrafía (ver `FontPair.script`).
 */
export function applyFontsToTheme(theme: PageTheme, headingFont: string, bodyFont: string, fontPairId = 'custom', options: { displayOnly?: boolean } = {}): PageTheme {
  const displayOnly = Boolean(options.displayOnly)
  const fontForRole = (role: string | undefined) => (
    role === 'paragraph' || role === 'smallText' || role === 'buttonPrimary' || role === 'buttonSecondary' || role === 'link'
      ? bodyFont
      : displayOnly && !DISPLAY_ROLES.has(role ?? '') ? bodyFont : headingFont
  )
  /* Una cursiva de caligrafía tiene un solo peso: en negrita se deformaba. */
  const display = (role: string) => (displayOnly && DISPLAY_ROLES.has(role) ? { fontWeight: '400', letterSpacing: '0' } : {})
  const projectTypography = (theme as Partial<ProjectTheme>).typographyComponents
  const typographyComponents = Array.isArray(projectTypography)
    ? {
        typographyComponents: projectTypography.map((item) => {
          const fontFamily = fontForRole(item.role)
          return {
            ...item,
            styles: { ...item.styles, fontFamily, ...display(item.role ?? '') },
            ...(item.breakpoints
              ? {
                  breakpoints: {
                    ...(item.breakpoints.tablet ? { tablet: { ...item.breakpoints.tablet, fontFamily } } : {}),
                    ...(item.breakpoints.mobile ? { mobile: { ...item.breakpoints.mobile, fontFamily } } : {}),
                  },
                }
              : {}),
          }
        }),
      }
    : {}
  const roleBreakpoints = theme.roleBreakpoints
    ? {
        roleBreakpoints: {
          ...theme.roleBreakpoints,
          tablet: Object.fromEntries(
            Object.entries(theme.roleBreakpoints.tablet ?? {}).map(([role, styles]) => [
              role,
              { ...styles, fontFamily: fontForRole(role) },
            ])
          ),
          mobile: Object.fromEntries(
            Object.entries(theme.roleBreakpoints.mobile ?? {}).map(([role, styles]) => [
              role,
              { ...styles, fontFamily: fontForRole(role) },
            ])
          ),
        },
      }
    : {}

  return {
    ...theme,
    presetId: 'custom',
    typography: {
      fontPairId,
      headingFont,
      bodyFont,
    },
    roles: {
      ...theme.roles,
      heading1: { ...theme.roles.heading1, fontFamily: headingFont, ...display('heading1') },
      heading2: { ...theme.roles.heading2, fontFamily: headingFont, ...display('heading2') },
      heading3: { ...theme.roles.heading3, fontFamily: fontForRole('heading3') },
      heading4: { ...theme.roles.heading4, fontFamily: fontForRole('heading4') },
      heading5: { ...theme.roles.heading5, fontFamily: fontForRole('heading5') },
      heading6: { ...theme.roles.heading6, fontFamily: fontForRole('heading6') },
      eyebrow: { ...theme.roles.eyebrow, fontFamily: fontForRole('eyebrow') },
      paragraph: { ...theme.roles.paragraph, fontFamily: bodyFont },
      smallText: { ...theme.roles.smallText, fontFamily: bodyFont },
      buttonPrimary: { ...theme.roles.buttonPrimary, fontFamily: bodyFont },
      buttonSecondary: { ...theme.roles.buttonSecondary, fontFamily: bodyFont },
      badge: { ...theme.roles.badge, fontFamily: fontForRole('badge') },
      stat: { ...theme.roles.stat, fontFamily: fontForRole('stat') },
      link: { ...theme.roles.link, fontFamily: bodyFont },
    },
    ...roleBreakpoints,
    ...typographyComponents,
  }
}

export function applyPresetToTheme(theme: PageTheme, preset: typeof PRESETS[number]): PageTheme {
  const palette = [
    preset.pal[4] ?? theme.colors.primary,
    preset.pal[1] ?? theme.colors.backgroundSecondary,
    preset.pal[2] ?? theme.colors.surface,
    preset.pal[0] ?? theme.colors.background,
    preset.pal[3] ?? theme.colors.muted,
    preset.pal[4] ?? theme.colors.text,
    preset.pal[5] ?? theme.colors.accent,
    preset.pal[0] ?? theme.colors.card,
  ]
  const themed = applyFontsToTheme(applyColorsToTheme(theme, palette, preset.id), `'${preset.f1}', sans-serif`, `'${preset.f2}', sans-serif`, preset.id)
  const cardStyles = {
    background: 'var(--wb-card)',
    backgroundColor: 'var(--wb-card)',
    borderColor: 'var(--wb-border)',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: '24px',
    boxShadow: 'none',
    padding: '42px',
  }

  if (!('cardComponents' in themed) || !Array.isArray(themed.cardComponents)) {
    return { ...themed, roles: { ...themed.roles, card: { ...themed.roles.card, ...cardStyles } } }
  }

  const projectTheme = themed as ProjectTheme
  return {
    ...projectTheme,
    roles: { ...projectTheme.roles, card: { ...projectTheme.roles.card, ...cardStyles } },
    cardComponents: projectTheme.cardComponents.map((component) => {
      if (component.id === 'card-default') {
        return { ...component, styles: { ...component.styles, ...cardStyles } }
      }
      if (component.id === 'card-soft') {
        return {
          ...component,
          styles: {
            ...component.styles,
            ...cardStyles,
            borderColor: 'color-mix(in srgb, var(--wb-border) 35%, transparent)',
            boxShadow: '0 6px 16px rgba(15,23,42,0.05)',
          },
        }
      }
      if (component.id === 'card-elevated') {
        return {
          ...component,
          styles: {
            ...component.styles,
            ...cardStyles,
            background: 'var(--wb-accent)',
            backgroundColor: 'var(--wb-accent)',
            borderColor: 'var(--wb-accent)',
          },
        }
      }
      return component
    }),
  } as ProjectTheme
}
